import { useMemo } from 'react';
import { Grid } from '../../core/layout/grid/grid';
import { Formik } from 'formik';
import { Button, ButtonColor, ButtonPull } from '../../core/button/button';
import { CreateTemplateFormProps, Locale, LOCALE_LABELS, MailContentTemplate } from '../types';
import { useTranslation } from 'next-i18next';
import { emailValidationSchema } from '../validations';
import { FormSelectInput } from '../../core/form/form-select-input';

export const CreateEmailTemplateForm = ({
  templateName,
  templates: existingTemplates,
  backendAdapter,
  onTemplateUpdated,
}: CreateTemplateFormProps<MailContentTemplate>) => {
  const { t } = useTranslation();
  const { error, updateTemplate } = backendAdapter.useUpdateTemplate({ templateName, onTemplateUpdated });

  const defaultTemplate = useMemo(() => {
    return existingTemplates?.length > 0
      ? existingTemplates.find((t) => t.locale === 'en-US') || existingTemplates[0]
      : { subject: '', body: '' };
  }, [existingTemplates]);

  const missingLocales = useMemo(() => {
    return Object.keys(LOCALE_LABELS).filter(
      (locale) => !existingTemplates.some((t) => t.locale === locale),
    ) as Locale[];
  }, [existingTemplates]);

  return (
    <Formik
      initialValues={{ subject: defaultTemplate.subject, body: defaultTemplate.body, locale: missingLocales[0] }}
      validationSchema={emailValidationSchema}
      validateOnChange={false}
      validateOnBlur={true}
      onSubmit={updateTemplate}
    >
      {({ handleSubmit, handleChange, handleBlur, isSubmitting, errors, values }) => (
        <Grid.Container fluid>
          {error && (
            <Grid.Row className="error-messages">
              <Grid.Column colspan={12}>
                <p>{error}</p>
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            <Grid.Column colspan={12}>
              <form onSubmit={handleSubmit}>
                <FormSelectInput
                  name="locale"
                  label={t('emailTemplates.selectLocale')}
                  fullWidth={true}
                  placeholderValue=""
                  options={missingLocales.reduce(
                    (locales, locale) => {
                      locales[locale] = LOCALE_LABELS[locale];

                      return locales;
                    },
                    {} as Record<string, string>,
                  )}
                  autoComplete="off"
                />
                <Button type="submit" pull={ButtonPull.Right} color={ButtonColor.Primary} loading={isSubmitting}>
                  {t('emailTemplates.create')}
                </Button>
              </form>
            </Grid.Column>
          </Grid.Row>
        </Grid.Container>
      )}
    </Formik>
  );
};
